import { Product } from '@/types/product';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Check, X, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

interface ComparisonTableSectionProps {
  products: Product[];
}

/**
 * Structured comparison table for Best-Of pages
 * Displays: Rank, Product, Circle Score, Price, Rating, Key Features
 */
export function ComparisonTableSection({ products }: ComparisonTableSectionProps) {
  if (products.length < 3) return null;

  return (
    <div className="border rounded-lg overflow-hidden bg-background">
      <div className="bg-muted p-4 border-b">
        <h3 className="text-xl font-bold">Side-by-Side Comparison</h3>
        <p className="text-sm text-muted-foreground mt-1">Compare features, pricing, and scores at a glance</p>
      </div>
      
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">Rank</TableHead>
              <TableHead className="min-w-[200px]">Product</TableHead>
              <TableHead className="text-center">Circle Score</TableHead>
              <TableHead className="text-center">Starting Price</TableHead>
              <TableHead className="text-center">Rating</TableHead>
              <TableHead className="min-w-[250px]">Top Features</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map((product, index) => {
              const hasScore = !!product.circleScore?.circle_score;
              const score = product.circleScore?.circle_score || 0;
              const topFeatures = product.best_for?.slice(0, 3) || [];

              return (
                <TableRow key={product.id}>
                  {/* Rank */}
                  <TableCell className="font-bold text-lg">
                    #{index + 1}
                  </TableCell>

                  {/* Product Name + Image */}
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {product.image && (
                        <img 
                          src={product.image} 
                          alt={product.title}
                          className="w-12 h-12 rounded object-cover"
                        />
                      )}
                      <div>
                        <Link to={`/product/${product.slug}`} className="font-semibold hover:text-primary">
                          {product.title}
                        </Link>
                        {product.is_select && (
                          <Badge variant="secondary" className="ml-2 text-xs">SELECT</Badge>
                        )}
                      </div>
                    </div>
                  </TableCell>

                  {/* Circle Score */}
                  <TableCell className="text-center">
                    {hasScore ? (
                      <div className="flex flex-col items-center">
                        <span className="text-2xl font-bold text-primary">{score.toFixed(1)}</span>
                        <span className="text-xs text-muted-foreground">/ 10</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">N/A</span>
                    )}
                  </TableCell>

                  {/* Price */}
                  <TableCell className="text-center">
                    {product.startingPrice ? (
                      <div>
                        <span className="font-semibold">${product.startingPrice}</span>
                        <span className="text-xs text-muted-foreground">/{product.price_duration || 'mo'}</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">Contact</span>
                    )}
                  </TableCell>

                  {/* Rating */}
                  <TableCell className="text-center">
                    {product.rating ? (
                      <div className="flex items-center justify-center gap-1">
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        <span className="font-semibold">{product.rating}</span>
                        <span className="text-xs text-muted-foreground">({product.review_count})</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">No reviews</span>
                    )}
                  </TableCell>

                  {/* Top Features */}
                  <TableCell>
                    <div className="space-y-1">
                      {topFeatures.map((feature, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm">
                          <Check className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </div>
                      ))}
                      {topFeatures.length === 0 && (
                        <span className="text-muted-foreground text-sm">Features not listed</span>
                      )}
                    </div>
                  </TableCell>

                  {/* Action */}
                  <TableCell className="text-right">
                    <Link to={`/product/${product.slug}`}>
                      <Button size="sm" variant="default">
                        View Details
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
